import type { Bounds, Surface } from './types';

const EXCLUDED =
  '[data-animate-ignore], animate-companion, input, textarea, select, [contenteditable], [role="dialog"], nav, header, footer';
const CANDIDATES = '[data-animate-surface], h1, h2, h3, article, figure, img, .card';

/** Reads page geometry; host content is only affected through cancellable animations. */
export class PageAdapter {
  bounds: Bounds = { left: 0, top: 0, width: 0, height: 0 };
  surfaces: Surface[] = [];
  dirty = true;
  private observer: MutationObserver;
  private resizeObserver: ResizeObserver;
  private animations = new Set<Animation>();
  private abort = new AbortController();

  constructor(private container?: HTMLElement) {
    const markDirty = () => {
      this.dirty = true;
    };
    const options = { passive: true, signal: this.abort.signal };
    window.addEventListener('resize', markDirty, options);
    window.addEventListener('scroll', markDirty, { ...options, capture: true });
    this.observer = new MutationObserver((records) => {
      if (
        records.some(
          (record) =>
            !(record.target instanceof Element && record.target.closest('animate-companion')),
        )
      )
        markDirty();
    });
    this.observer.observe(container ?? document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style'],
    });
    this.resizeObserver = new ResizeObserver(markDirty);
    this.resizeObserver.observe(container ?? document.documentElement);
    this.refresh();
  }

  refresh(): void {
    this.dirty = false;
    const rect = this.container?.getBoundingClientRect();
    this.bounds = rect
      ? { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    const { left, top, width, height } = this.bounds;
    this.surfaces = [];
    const candidates = (this.container ?? document.body).querySelectorAll<HTMLElement>(CANDIDATES);
    for (const element of Array.from(candidates).slice(0, 160)) {
      if (element.closest(EXCLUDED) || !element.checkVisibility({ checkVisibilityCSS: true }))
        continue;
      if (element.querySelector('input, textarea, select, [contenteditable], iframe, video'))
        continue;
      const box = element.getBoundingClientRect();
      const x = box.left - left;
      const y = box.top - top;
      if (
        box.width < 80 ||
        box.height < 18 ||
        y < 70 ||
        y > height - 30 ||
        x + box.width < 40 ||
        x > width - 40
      )
        continue;
      this.surfaces.push({ element, x, y, width: box.width, height: box.height });
    }
  }

  /** Existing supports remain valid outside the viewport; only new targets are filtered. */
  measureSurface(element: HTMLElement | undefined): Surface | undefined {
    if (!element?.isConnected || !element.checkVisibility({ checkVisibilityCSS: true }))
      return undefined;
    const box = element.getBoundingClientRect();
    if (!box.width || !box.height) return undefined;
    return {
      element,
      x: box.left - this.bounds.left,
      y: box.top - this.bounds.top,
      width: box.width,
      height: box.height,
    };
  }

  impact(element: HTMLElement, direction: number): void {
    if (!element.isConnected) return;
    const animation = element.animate(
      [
        { transform: 'translate(0, 0) rotate(0deg)' },
        {
          transform: `translate(${direction * 22}px, -9px) rotate(${direction * 3}deg)`,
          offset: 0.16,
        },
        {
          transform: `translate(${-direction * 12}px, 3px) rotate(${-direction * 1.5}deg)`,
          offset: 0.35,
        },
        {
          transform: `translate(${direction * 6}px, -2px) rotate(${direction * 0.7}deg)`,
          offset: 0.57,
        },
        { transform: 'translate(0, 0) rotate(0deg)' },
      ],
      { duration: 900, easing: 'ease-out', composite: 'add' },
    );
    this.track(animation);
  }

  /** A playful notebook effect. No text is read and host styles remain untouched. */
  haunt(element: HTMLElement): void {
    if (!element.isConnected) return;
    const style = getComputedStyle(element);
    const opacity = Number(style.opacity);
    const filter = style.filter === 'none' ? '' : `${style.filter} `;
    const transform = style.transform === 'none' ? '' : `${style.transform} `;
    this.track(
      element.animate(
        [
          { opacity, filter: `${filter}grayscale(0)`, transform: `${transform}translateX(0)` },
          {
            opacity: opacity * 0.35,
            filter: `${filter}grayscale(1)`,
            transform: `${transform}translateX(-5px)`,
            offset: 0.25,
          },
          {
            opacity: opacity * 0.55,
            filter: `${filter}grayscale(1)`,
            transform: `${transform}translateX(5px)`,
            offset: 0.4,
          },
          {
            opacity: opacity * 0.4,
            filter: `${filter}grayscale(1)`,
            transform: `${transform}translateX(-3px)`,
            offset: 0.55,
          },
          { opacity, filter: `${filter}grayscale(0)`, transform: `${transform}translateX(0)` },
        ],
        { duration: 1300, easing: 'ease-in-out' },
      ),
    );
  }

  /** Additive translation preserves the host's own transform and never affects layout. */
  gravity(element: HTMLElement, x: number, y: number, duration = 1200): Animation | undefined {
    if (!element.isConnected) return undefined;
    const animation = element.animate(
      [
        { translate: '0px 0px' },
        { translate: `${x}px ${y}px`, offset: 0.28 },
        { translate: `${x}px ${y}px`, offset: 0.46 },
        { translate: `${-x * 0.12}px ${-y * 0.12}px`, offset: 0.78 },
        { translate: '0px 0px' },
      ],
      { duration, easing: 'ease-in-out', composite: 'add' },
    );
    this.track(animation);
    return animation;
  }

  private track(animation: Animation): void {
    this.animations.add(animation);
    void animation.finished
      .catch(() => {})
      .finally(() => {
        animation.cancel();
        this.animations.delete(animation);
      });
  }

  pause(): void {
    for (const animation of this.animations) animation.pause();
  }
  resume(): void {
    for (const animation of this.animations) animation.play();
  }
  restore(): void {
    for (const animation of this.animations) animation.cancel();
    this.animations.clear();
  }
  destroy(): void {
    this.abort.abort();
    this.observer.disconnect();
    this.resizeObserver.disconnect();
    this.restore();
  }
}
